import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, X, MapPin, Briefcase, Sparkles, SlidersHorizontal, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import VerifiedBadge from "../components/VerifiedBadge";

function profileImage(profile) {
  if (profile.photos && profile.photos.length > 0) {
    return fileUrl(profile.photos[0]);
  }
  if (profile.photo_url) return profile.photo_url;
  return null;
}

export default function Discover() {
  const { user } = useAuth();
  const isPremium = user?.subscription_tier === "premium";
  const [profiles, setProfiles] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ age_min: "", age_max: "", gender: "", verified_only: false });

  const loadProfiles = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/discover", { params });
      setProfiles(data);
      setIndex(0);
    } catch {
      toast.error("Couldn't load profiles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfiles(); }, []);

  const applyFilters = () => {
    if (!isPremium) {
      toast.error("Advanced filters are a Premium feature.");
      return;
    }
    const p = {};
    if (filters.age_min) p.age_min = Number(filters.age_min);
    if (filters.age_max) p.age_max = Number(filters.age_max);
    if (filters.gender) p.gender = filters.gender;
    if (filters.verified_only) p.verified_only = true;
    loadProfiles(p);
    setFiltersOpen(false);
  };

  const resetFilters = () => {
    setFilters({ age_min: "", age_max: "", gender: "", verified_only: false });
    loadProfiles();
    setFiltersOpen(false);
  };

  const current = profiles[index];

  const act = async (action) => {
    if (!current) return;
    setDirection(action === "like" ? 1 : -1);
    const target = current.user_id;
    try {
      if (action === "like") {
        const { data } = await api.post(`/like/${target}`);
        if (data.matched) {
          toast.success(`It's a match with ${current.name}! ✨`, { duration: 4000 });
        }
      } else {
        await api.post(`/pass/${target}`);
      }
    } catch {
      toast.error("Action failed");
    }
    setTimeout(() => setIndex((i) => i + 1), 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-gold-soft tracking-overline text-xs">LOADING PROFILES</div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="discover-empty">
        <div className="glass-strong rounded-3xl p-12">
          <Sparkles className="mx-auto text-gold mb-6" size={32} />
          <h2 className="font-display text-3xl font-light mb-4">That's everyone for now.</h2>
          <p className="text-white/60 leading-relaxed">
            We curate carefully — check back tomorrow for new thoughtfully selected matches.
          </p>
          <button
            onClick={() => { setIndex(0); window.location.reload(); }}
            className="btn-outline-gold mt-8"
            data-testid="discover-refresh-btn"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 md:py-12" data-testid="discover-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-gold-soft tracking-overline text-xs mb-2">DISCOVER</div>
          <h1 className="font-display text-3xl sm:text-4xl font-light">Curated for you today.</h1>
        </div>
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:border-[#D4AF37]/40 hover:text-gold transition-colors relative"
          data-testid="filters-toggle"
          aria-label="Filters"
        >
          <SlidersHorizontal size={16} />
          {!isPremium && (
            <Lock size={9} className="absolute -top-0.5 -right-0.5 text-gold bg-[#050505] rounded-full p-0.5 w-3.5 h-3.5" />
          )}
        </button>
      </div>

      {filtersOpen && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-strong rounded-3xl p-6 mb-6"
          data-testid="filters-panel"
        >
          {!isPremium && (
            <div className="mb-4 p-3 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-xs text-white/70 flex items-center gap-2">
              <Lock size={12} className="text-gold" />
              <span>Advanced filters are a Premium feature. <Link to="/premium" className="text-gold underline-offset-2 hover:underline">See membership</Link></span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input
              type="number" placeholder="Min age" min="18" max="99"
              value={filters.age_min}
              onChange={(e) => setFilters({ ...filters, age_min: e.target.value })}
              disabled={!isPremium}
              className="input-luxe disabled:opacity-50"
              data-testid="filter-age-min"
            />
            <input
              type="number" placeholder="Max age" min="18" max="99"
              value={filters.age_max}
              onChange={(e) => setFilters({ ...filters, age_max: e.target.value })}
              disabled={!isPremium}
              className="input-luxe disabled:opacity-50"
              data-testid="filter-age-max"
            />
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { v: "", l: "Anyone" },
              { v: "woman", l: "Women" },
              { v: "man", l: "Men" },
              { v: "non-binary", l: "Non-binary" },
            ].map((g) => (
              <button
                key={g.v || "any"}
                onClick={() => isPremium && setFilters({ ...filters, gender: g.v })}
                disabled={!isPremium}
                className={`px-3 py-1.5 rounded-full text-xs border transition-all disabled:opacity-50 ${
                  filters.gender === g.v ? "border-[#D4AF37] text-gold bg-[#D4AF37]/10" : "border-white/15 text-white/70"
                }`}
                data-testid={`filter-gender-${g.v || "any"}`}
              >
                {g.l}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={filters.verified_only}
              onChange={(e) => isPremium && setFilters({ ...filters, verified_only: e.target.checked })}
              disabled={!isPremium}
              className="w-4 h-4 accent-[#D4AF37]"
              data-testid="filter-verified-only"
            />
            Show only verified members
          </label>
          <div className="flex gap-3">
            <button onClick={applyFilters} disabled={!isPremium} className="btn-gold flex-1 text-sm disabled:opacity-50" data-testid="apply-filters-btn">
              Apply
            </button>
            <button onClick={resetFilters} className="btn-outline-gold text-sm" data-testid="reset-filters-btn">
              Reset
            </button>
          </div>
        </motion.div>
      )}

      <div className="relative h-[640px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.user_id}
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: direction * 200, rotate: direction * 8 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
            data-testid="profile-card"
          >
            <div className="relative h-full rounded-3xl overflow-hidden glass-strong">
              {profileImage(current) ? (
                <img src={profileImage(current)} alt={current.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
                  <span className="font-display text-7xl text-gold/40">{current.name?.[0]}</span>
                </div>
              )}

              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />

              <div className="absolute bottom-0 inset-x-0 p-8">
                <div className="flex items-baseline gap-3 mb-3">
                  <h2 className="font-display text-4xl font-light" data-testid="profile-name">{current.name}</h2>
                  <span className="text-2xl text-white/70 font-light">{current.age}</span>
                  {current.verified && <VerifiedBadge size={20} className="ml-1" />}
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80 mb-4">
                  {current.location && (
                    <span className="flex items-center gap-1.5"><MapPin size={14} className="text-gold" />{current.location}</span>
                  )}
                  {current.occupation && (
                    <span className="flex items-center gap-1.5"><Briefcase size={14} className="text-gold" />{current.occupation}</span>
                  )}
                </div>
                <p className="text-white/85 leading-relaxed font-light text-[15px] line-clamp-3">{current.bio}</p>

                {current.interests?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-5">
                    {current.interests.slice(0, 5).map((t) => (
                      <span key={t} className="px-3 py-1 rounded-full border border-white/20 text-xs text-white/80 bg-black/30 backdrop-blur-sm">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-center gap-6 mt-8">
        <button
          onClick={() => act("pass")}
          className="w-16 h-16 rounded-full bg-[#0f0f0f] border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-all hover:-translate-y-0.5"
          aria-label="Pass"
          data-testid="pass-btn"
        >
          <X size={26} />
        </button>
        <button
          onClick={() => act("like")}
          className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#050505] shadow-lg shadow-[#D4AF37]/30 hover:shadow-[#D4AF37]/50 hover:bg-[#E6C96C] transition-all hover:-translate-y-1"
          aria-label="Like"
          data-testid="like-btn"
        >
          <Heart size={28} fill="#050505" />
        </button>
        <button
          onClick={() => act("pass")}
          className="w-16 h-16 rounded-full bg-[#0f0f0f] border border-[#D4AF37]/30 flex items-center justify-center text-gold hover:border-[#D4AF37]/60 transition-all hover:-translate-y-0.5"
          aria-label="Super like (pass for now)"
          data-testid="superlike-btn"
        >
          <Sparkles size={22} />
        </button>
      </div>

      <div className="text-center mt-6 text-xs text-white/30 tracking-overline">
        {index + 1} OF {profiles.length}
      </div>
    </div>
  );
}
